import { useState, useEffect, useContext } from 'react'

import LoadingContext from '../../context/Loading/Loading'

import {
  formatNumber,
  formatCurrency,
  formatPhoneNumber,
  capitalizeString,
  formatString,
  formatTicketGroups,
  getDefaultSeason,
} from '../../utilities/helpers'

import { getSeasons, getUserBalanceReport } from '../../utilities/api'
import { useReportRefresh } from '../../hooks/useReportRefresh'

import Tabs from 'react-bootstrap/Tabs'
import Tab from 'react-bootstrap/Tab'
import Card from 'react-bootstrap/Card'
import Table from 'react-bootstrap/Table'
import Stack from 'react-bootstrap/Stack'

import { ReportActions } from '../ReportActions'
import { EmptyContainer } from '../EmptyContainer'
import { LoadingContainer } from '../LoadingContainer'
import { PageLoadingContainer } from '../PageLoadingContainer'
import { SeasonSelector } from '../SeasonSelector'

export default function UserBalanceTotalsWrapper({ organization }) {
  const { isLoading, showLoading, hideLoading } = useContext(LoadingContext)

  const [isLoadingData, setIsLoadingData] = useState(false)
  const { refreshToken, refresh, completeRefresh } =
    useReportRefresh(isLoadingData)

  const [seasons, setSeasons] = useState()

  const [selectedSeason, setSelectedSeason] = useState()

  const [userBalanceTotals, setUserBalanceTotals] = useState(null)

  const [fromDate, setFromDate] = useState(null)

  const [toDate, setToDate] = useState(null)

  useEffect(() => {
    showLoading()
    loadSeasons(organization.uuid)
  }, [])

  useEffect(() => {
    const fetchData = async () => {
      if (!selectedSeason || !fromDate || !toDate) return

      setIsLoadingData(true)

      try {
        const res = await getUserBalanceReport(
          organization.uuid,
          fromDate?.toISOString() || null,
          toDate?.toISOString() || null
        )

        const ordersByEvent = res.data.reduce((acc, order) => {
          const eventId = order.eventId
          if (!acc[eventId]) {
            acc[eventId] = []
          }
          acc[eventId].push(order)
          return acc
        }, {})

        const groupedArr = Object.entries(ordersByEvent).map(
          ([eventId, orders]) => ({
            eventId,
            eventName: orders[0]?.eventName || '',
            orders,
          })
        )

        setUserBalanceTotals(groupedArr)
      } catch (error) {
        console.error('Error fetching user balance totals:', error)
      } finally {
        setIsLoadingData(false)
        completeRefresh()
      }
    }

    fetchData()
  }, [selectedSeason, fromDate, toDate, organization.uuid, refreshToken])

  const loadSeasons = () => {
    getSeasons(organization.uuid)
      .then((res) => {
        setSeasons(res.data)
        // select the current season - start and end time is in between current time
        const selected = getDefaultSeason(res.data)

        if (selected) setSelectedSeason(selected?.id)
      })
      .catch((err) => console.error(err))
      .finally(() => hideLoading())
  }

  const handleDownloadExcel = () => {
    console.log('Download Excel report', {
      fromDate,
      toDate,
    })
    // TODO: Implement Excel download functionality
  }

  const handleDownloadPDF = () => {
    console.log('Download PDF report', {
      fromDate,
      toDate,
    })
    // TODO: Implement PDF download functionality
  }

  const handleDateRangeChange = (dates) => {
    const [start, end] = dates
    setFromDate(start)
    setToDate(end)
  }

  return (
    <>
      {isLoading ? (
        <PageLoadingContainer style="without-sidebar" />
      ) : (
        <section className="spacer-sm">
          <header className="section-header-sm">
            <div className="section-heading">
              <h1>User Balance Totals</h1>
            </div>
            <p className="section-header-desc">
              Access control and attendance data for the selected season.
            </p>
          </header>
          {seasons?.length > 0 ? (
            <>
              <ReportActions
                isLoading={isLoadingData}
                onRefresh={refresh}
                isRefreshing={isLoadingData}
                fromDate={fromDate}
                toDate={toDate}
                onDateRangeChange={handleDateRangeChange}
              >
                <SeasonSelector
                  isLoading={isLoadingData}
                  seasons={seasons}
                  selected={selectedSeason}
                  setSelected={setSelectedSeason}
                />
              </ReportActions>
              <Results
                isLoading={isLoadingData}
                selectedSeason={selectedSeason}
                toDate={toDate}
                fromDate={fromDate}
                data={userBalanceTotals}
              />
            </>
          ) : (
            <Card body>
              <EmptyContainer style="center lg report">
                <p>No seasons created</p>
                <p className="subtitle subtitle--dark">
                  Create a season to start tracking its event's sales and
                  attendance data.
                </p>
              </EmptyContainer>
            </Card>
          )}
        </section>
      )}
    </>
  )
}

const EmptyRow = ({ cols }) => (
  <tr className="white">
    <td colSpan={cols} className="td--empty"></td>
  </tr>
)

function UserBalanceSummaryTable({ balance }) {
  const EventRow = ({ event }) => {
    const isMonthlyTotal = event?.isMonthlyTotal

    return (
      <>
        <tr className={`md-x ${isMonthlyTotal ? 'total-row dark' : ''}`}>
          <td colSpan={2}>{'2425NIDG1006' || ''}</td>
          <td colSpan={2} className="text-left">
            Sault Ste. Marie Greyhounds
          </td>
          <td className="text-left">08-Sep-2024 7:30 PM</td>
          <td>{formatNumber(3)}</td>
          <td>{formatNumber(953)}</td>
          <td>{formatCurrency(75)}</td>
          <td>{formatNumber(1028)}</td>
          <td>{formatCurrency(894)}</td>
          <td>{formatNumber(134)}</td>
          <td>{formatCurrency(4101)}</td>
        </tr>
        {isMonthlyTotal && <EmptyRow cols="12" />}
      </>
    )
  }

  const SummaryRow = () => {
    return (
      <tr className="md-x total-row light">
        <td />
        <td>{formatNumber(277)}</td>
        <td>{formatNumber(4155)}</td>
        <td>{formatCurrency(81909.6)}</td>
        <td>{formatNumber(856)}</td>
        <td>{formatCurrency(14518.42)}</td>
        <td>{formatNumber(3576)}</td>
        <td>{formatCurrency(67291.18)}</td>
        <td>{formatCurrency(3078)}</td>
        <td>{formatCurrency(70369.18)}</td>
        <td>{formatCurrency(70369.18)}</td>
        <td>{formatCurrency(0)}</td>
      </tr>
    )
  }

  const SummaryHeader = () => (
    <tr className="md">
      <th className="uppercase text-body xs">Totals</th>
      <th className="uppercase text-body xs">Comps</th>
      <th className="uppercase text-body sm">Sold tickets</th>
      <th className="uppercase text-body sm">Sold amount</th>
      <th className="uppercase text-body md">Return tickets</th>
      <th className="uppercase text-body md">Return amount</th>
      <th className="uppercase text-body xs">Net tickets</th>
      <th className="uppercase text-body sm">Net amount</th>
      <th className="uppercase text-body sm">Service fees</th>
      <th className="uppercase text-body md">Gross amount</th>
      <th className="uppercase text-body lg">Ticket proceeds</th>
      <th className="uppercase text-body md">Deferred amt.</th>
    </tr>
  )

  const TableHeader = () => (
    <thead>
      <SummaryHeader />
      <SummaryRow />
      <EmptyRow cols="12" />
      <tr className="md">
        <th colSpan={2} className="uppercase text-body">
          Event code
        </th>
        <th colSpan={2} className="uppercase text-body text-left">
          Event description
        </th>
        <th className="uppercase text-body text-left">Date/Time</th>
        <th className="uppercase text-body">Comps</th>
        <th className="uppercase text-body">Sold tickets</th>
        <th className="uppercase text-body">Sold amount</th>
        <th className="uppercase text-body">Refund tickets</th>
        <th className="uppercase text-body">Refund amount</th>
        <th className="uppercase text-body">Net tickets</th>
        <th className="uppercase text-body">Net Amount</th>
      </tr>
    </thead>
  )

  return (
    <Card body className="card--md">
      <div className="card-body-heading card-body-heading--xs">
        <Card.Title as="h5">Summary</Card.Title>
      </div>
      <div className="table-container border-bottom-0">
        <Table>
          <TableHeader />
          <tbody>
            <EventRow />
            {/* {balance?.map((listing, index, listings) => (
                          <Fragment key={index}>
                          {listing?.orders?.map((order, idx) => (
                            <Listing key={idx} listing={order} />
                        ))}
                              {/* Render EmptyRow only if there is a next listing */}
            {/* {index < listings.length - 1 && (
                            <EmptyRow />
                        )}
                        </Fragment>
                      ))} */}
          </tbody>
          <tfoot>
            <tr className="md total-row dark">
              <td colSpan={2} className="td--empty"></td>
              <td colSpan={2} className="text-left text-uppercase">
                Report Totals
              </td>
              <td className="td--empty"></td>
              <td>{formatNumber(271)}</td>
              <td>{formatNumber(1200)}</td>
              <td>{formatCurrency(83)}</td>
              <td>{formatNumber(1500)}</td>
              <td>{formatCurrency(950)}</td>
              <td>{formatNumber(145)}</td>
              <td>{formatCurrency(4450)}</td>
            </tr>
          </tfoot>
        </Table>
      </div>
    </Card>
  )
}

function ChangesAndPaymentMethodsTable({ chargesPayments }) {
  const ChargesTable = () => {
    return (
      <div className="table-container border-bottom-0">
        <Table>
          <thead>
            <tr className="md">
              <th className="xxl uppercase text-body">Service charge</th>
              <th className="uppercase text-body text-left">Description</th>
              <th className="uppercase text-body">Amount</th>
            </tr>
          </thead>
          <tbody>
            <tr className="md-x">
              <td>{'NID_Single' || ''}</td>
              <td className="text-left">Per Single Ticket Print Fee</td>
              <td>{formatCurrency(87.5)}</td>
            </tr>
            {/* {balance?.map((listing, index, listings) => (
                          <Fragment key={index}>
                          {listing?.orders?.map((order, idx) => (
                            <Listing key={idx} listing={order} />
                        ))}
                              {/* Render EmptyRow only if there is a next listing */}
            {/* {index < listings.length - 1 && (
                            <EmptyRow />
                        )}
                        </Fragment>
                      ))} */}
          </tbody>
          <tfoot>
            <tr className="md-x total-row dark">
              <td></td>
              <td className="text-left">Totals:</td>
              <td>{formatCurrency(3078)}</td>
            </tr>
          </tfoot>
        </Table>
      </div>
    )
  }

  const PaymentMethodsTable = () => {
    return (
      <div className="table-container border-bottom-0">
        <Table>
          <thead>
            <tr className="md">
              <th className="xxl uppercase text-body">Pay method</th>
              <th className="xxl uppercase text-body text-left">Description</th>
              <th className="sm uppercase text-body">Payments</th>
              <th className="uppercase text-body">Amount</th>
            </tr>
          </thead>
          <tbody>
            <tr className="md-x">
              <td>{'AMEX' || ''}</td>
              <td className="text-left">American Express</td>
              <td>{formatNumber(18)}</td>
              <td>{formatCurrency(212.5)}</td>
            </tr>
          </tbody>
          <tfoot>
            <tr className="md-x total-row dark">
              <td></td>
              <td className="text-left">Totals:</td>
              <td>{formatNumber(672)}</td>
              <td>{formatCurrency(70369.18)}</td>
            </tr>
          </tfoot>
        </Table>
      </div>
    )
  }

  return (
    <Card body className="card--md">
      <div className="card-body-heading card-body-heading-sm">
        <Card.Title as="h5">Summary</Card.Title>
      </div>
      <Stack gap={4}>
        <ChargesTable />
        <PaymentMethodsTable />
      </Stack>
    </Card>
  )
}

function Results({ isLoading, data, selectedSeason, toDate, fromDate }) {
  const [key, setKey] = useState('summary')

  const handleKey = (k) => {
    setKey(k)
  }

  if (!selectedSeason || !toDate || !fromDate) {
    return (
      <Card body>
        <EmptyContainer style="center lg report">
          <p>Get started by choosing your filters</p>
          <p className="subtitle subtitle--dark">
            Select an event, season or date range from the options above. Once
            you've made your selections, your report will appear right away.
          </p>
        </EmptyContainer>
      </Card>
    )
  }

  if (isLoading) {
    return <LoadingContainer />
  }

  if (!data || data?.length === 0) {
    return (
      <Card body>
        <EmptyContainer style="center lg report">
          <p>No data</p>
          <p className="subtitle subtitle--dark">
            There's currently no data for the{' '}
            {fromDate && toDate
              ? 'specified date range. Please select a different date range.'
              : 'season. Try selecting a date range.'}
          </p>
        </EmptyContainer>
      </Card>
    )
  }

  return (
    <Tabs
      defaultActiveKey="sections"
      variant="pills"
      activeKey={key}
      onSelect={handleKey}
      className="w-max-content nav-pills-default"
    >
      <Tab eventKey="summary" title="Summary">
        <UserBalanceSummaryTable balance={data} />
      </Tab>
      <Tab eventKey="charges-methods" title="Service charges & Payment methods">
        <ChangesAndPaymentMethodsTable chargesPayments={data} />
      </Tab>
    </Tabs>
  )
}
